import { useEffect, useMemo, useState } from "react";
import { feesApi, invoicesApi, ApiError } from "@/api";
import type { Fee, InvoiceCreateInput, InvoiceCreateLine, InvoiceCreateResponse } from "@/types";
import { Button, Spinner, TextField } from "@/components/ui";
import { formatMoney } from "@/lib/money";
import { FormDrawer } from "./FormDrawer";

type LineDraft = { selected: boolean; amount: string; description: string };

function remainingOf(fee: Fee): number {
  return Math.max(0, fee.amount - fee.amount_invoiced);
}

/** 50% of the fee, rounded to 2dp. */
function halfOf(fee: Fee): number {
  return Math.round(fee.amount * 50) / 100;
}

/** Create an invoice by billing one or more of the job's fees (Brief 05 §3.3).
 *  Raises a LIVE Xero invoice — confirm-gated and hard-guarded against
 *  double-submit. On success the parent refreshes BOTH the invoices list and
 *  the fees data (create mutates fees). */
export function CreateInvoiceDrawer({
  jobId,
  leadClientName,
  defaultReference,
  onClose,
  onSaved,
}: {
  jobId: number;
  leadClientName: string | null;
  defaultReference: string;
  onClose: () => void;
  onSaved: (res: InvoiceCreateResponse) => void;
}) {
  const [billable, setBillable] = useState<Fee[]>([]);
  const [drafts, setDrafts] = useState<Record<number, LineDraft>>({});
  const [loadingFees, setLoadingFees] = useState(true);
  const [feesError, setFeesError] = useState<string | undefined>();

  const [issueDate, setIssueDate] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [reference, setReference] = useState(defaultReference);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | undefined>();

  // Load the job's fees; the line picker is the fees with remaining > 0.
  useEffect(() => {
    let cancelled = false;
    setLoadingFees(true);
    feesApi
      .listFees(jobId)
      .then((res) => {
        if (cancelled) return;
        const open = (res.items ?? []).filter((f) => remainingOf(f) > 0);
        setBillable(open);
        const seed: Record<number, LineDraft> = {};
        for (const f of open) {
          seed[f.id] = { selected: false, amount: String(remainingOf(f)), description: f.description };
        }
        setDrafts(seed);
      })
      .catch((err) => {
        if (!cancelled) setFeesError(err instanceof ApiError ? err.message : "Could not load fees.");
      })
      .finally(() => {
        if (!cancelled) setLoadingFees(false);
      });
    return () => {
      cancelled = true;
    };
  }, [jobId]);

  function patchDraft(feeId: number, patch: Partial<LineDraft>) {
    setDrafts((d) => ({ ...d, [feeId]: { ...d[feeId], ...patch } }));
  }

  const total = useMemo(
    () =>
      billable.reduce((sum, f) => {
        const d = drafts[f.id];
        if (!d?.selected) return sum;
        const n = Number(d.amount);
        return sum + (Number.isFinite(n) ? n : 0);
      }, 0),
    [billable, drafts],
  );

  const selectedCount = billable.filter((f) => drafts[f.id]?.selected).length;

  function buildLines(): { lines: InvoiceCreateLine[] } | { error: string } {
    const lines: InvoiceCreateLine[] = [];
    for (const f of billable) {
      const d = drafts[f.id];
      if (!d?.selected) continue;
      const amt = Number(d.amount);
      const remaining = remainingOf(f);
      if (!d.amount.trim() || Number.isNaN(amt) || amt <= 0) {
        return { error: `Enter an amount for “${f.description}”.` };
      }
      if (amt > remaining) {
        return { error: `“${f.description}”: can't bill more than ${formatMoney(remaining)} remaining.` };
      }
      lines.push({ fee_id: f.id, amount: amt, description: d.description.trim() || undefined });
    }
    if (lines.length === 0) return { error: "Select at least one fee to bill." };
    return { lines };
  }

  async function submit() {
    if (saving) return;
    const built = buildLines();
    if ("error" in built) {
      setError(built.error);
      return;
    }
    const confirmed = window.confirm(
      `Raise a live invoice for ${formatMoney(total)} to ${leadClientName || "the lead client"} in Xero?\n\nThis bills the client for real and can't be undone from here.`,
    );
    if (!confirmed) return;

    setSaving(true);
    setError(undefined);
    const input: InvoiceCreateInput = {
      job_id: jobId,
      lines: built.lines,
      issue_date: issueDate || undefined,
      due_date: dueDate || undefined,
      reference: reference.trim() || undefined,
    };
    try {
      const res = await invoicesApi.createInvoice(input);
      onSaved(res);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not raise the invoice.");
      setSaving(false);
    }
  }

  const noBillable = !loadingFees && !feesError && billable.length === 0;

  return (
    <FormDrawer
      title="New invoice"
      busy={saving}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button variant="primary" onClick={submit} loading={saving} disabled={loadingFees || noBillable || selectedCount === 0}>
            Raise invoice{total > 0 ? ` · ${formatMoney(total)}` : ""}
          </Button>
        </>
      }
    >
      {error ? <p className="leads__error" role="alert" onClick={() => setError(undefined)}>{error}</p> : null}

      <p className="job-drawer__muted">
        Billing to <strong>{leadClientName || "the lead client"}</strong>. Submitting raises a live AUTHORISED invoice in Xero.
      </p>

      <section className="job-drawer__section">
        <h3>Fees to bill</h3>
        {loadingFees ? (
          <div className="jobs-list__loading"><Spinner label="Loading fees…" /></div>
        ) : feesError ? (
          <p className="leads__error" role="alert">{feesError}</p>
        ) : noBillable ? (
          <p className="job-drawer__muted">No billable fees — every fee is fully invoiced. Add or adjust fees first.</p>
        ) : (
          <ul className="inv-line-picker">
            {billable.map((f) => {
              const d = drafts[f.id];
              const remaining = remainingOf(f);
              return (
                <li key={f.id} className={`inv-line${d?.selected ? " inv-line--on" : ""}`}>
                  <label className="inv-line__check">
                    <input
                      type="checkbox"
                      checked={!!d?.selected}
                      onChange={(e) => patchDraft(f.id, { selected: e.target.checked })}
                    />
                    <span className="inv-line__desc">{f.description}</span>
                  </label>
                  <span className="job-drawer__muted inv-line__remaining">{formatMoney(remaining)} remaining</span>
                  {d?.selected ? (
                    <div className="inv-line__edit">
                      <div className="inv-line__amount">
                        <TextField
                          label="Amount (£)"
                          type="number"
                          step="0.01"
                          min={0}
                          max={remaining}
                          value={d.amount}
                          onChange={(e) => patchDraft(f.id, { amount: e.target.value })}
                        />
                        {/* 50% shortcut — only for fees not yet invoiced (pending). */}
                        {f.amount_invoiced === 0 ? (
                          <button
                            type="button"
                            className="inv-line__half"
                            onClick={() => patchDraft(f.id, { amount: String(halfOf(f)) })}
                          >
                            Bill 50% ({formatMoney(halfOf(f))})
                          </button>
                        ) : null}
                      </div>
                      <TextField
                        label="Description"
                        value={d.description}
                        onChange={(e) => patchDraft(f.id, { description: e.target.value })}
                      />
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <section className="job-drawer__section">
        <h3>Details</h3>
        <div className="lead-drawer__two">
          <TextField label="Issue date" type="date" value={issueDate} onChange={(e) => setIssueDate(e.target.value)} hint="Defaults to today." />
          <TextField label="Due date" type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} hint="Defaults to issue + 3 days." />
        </div>
        <TextField label="Reference" value={reference} onChange={(e) => setReference(e.target.value)} hint="Defaults to the job name." />
      </section>

      <p className="inv-total">Total to bill: <strong className="numeric">{formatMoney(total)}</strong></p>
    </FormDrawer>
  );
}
